// ════════════════════════════════════════════════════════════
// FacturasPagar — facturas de proveedor: pendientes, pagadas e histórico
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, Button, Chip, Alert, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getFacturasProveedor, descargarArchivoFacturaProveedor } from '../api';
import RegistrarFacturaProveedor from './RegistrarFacturaProveedor';

const ESTADOS: any = {
  pendiente_recibir: { label: 'Pendiente de recibir', color: 'warning' },
  registrada:        { label: 'Pendiente de pago',    color: 'info' },
  pagada:            { label: 'Pagada',               color: 'success' },
};
const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

export default function FacturasPagar() {
  const [datos, setDatos] = useState<any>({ items: [], resumen: {} });
  const [vista, setVista] = useState(0);        // 0 pendientes · 1 pagadas · 2 todas
  const [search, setSearch] = useState('');
  const [cargando, setCargando] = useState(true);
  const [registrar, setRegistrar] = useState<any>(null);

  const cargar = () => {
    setCargando(true);
    getFacturasProveedor({ search })
      .then(setDatos).catch(() => setDatos({ items: [], resumen: {} }))
      .finally(() => setCargando(false));
  };
  useEffect(() => {
    const t = setTimeout(cargar, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const r = datos.resumen || {};
  const hoy = new Date().toISOString().slice(0, 10);
  const todas: any[] = datos.items || [];
  const pendientes = todas.filter((f: any) => f.estado !== 'pagada');
  const pagadas = todas.filter((f: any) => f.estado === 'pagada');
  const lista = vista === 0 ? pendientes : vista === 1 ? pagadas : todas;
  const totalPagado = pagadas.reduce((s: number, f: any) => s + (f.total || 0), 0);

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #f59e0b' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pendientes de recibir del proveedor</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {r.pendientes_recibir || 0} · {eur(r.importe_pendiente_recibir)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #dc2626' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Registradas pendientes de pago</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#dc2626' }}>{eur(r.pendiente_pago)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #16a34a' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pagadas</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#16a34a' }}>
                {pagadas.length} · {eur(totalPagado)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Tabs value={vista} onChange={(_, v) => setVista(v)} sx={{ flex: 1, minWidth: 280 }}>
          <Tab label={`Pendientes (${pendientes.length})`} />
          <Tab label={`Pagadas (${pagadas.length})`} />
          <Tab label="Todas" />
        </Tabs>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setRegistrar('nueva')}>
          Factura sin pedido
        </Button>
      </Box>
      <TextField fullWidth size="small" label="Buscar (proveedor, nº factura, pedido)" sx={{ mb: 2 }}
        value={search} onChange={(e) => setSearch(e.target.value)} />

      {cargando ? <CircularProgress /> : lista.length === 0 ? (
        <Alert severity="info">
          {vista === 1
            ? 'Todavía no hay facturas pagadas.'
            : 'No hay facturas. Aparecen aquí automáticamente al emitir un pedido a proveedor en Compras.'}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Proveedor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Factura / Pedido</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{vista === 1 ? 'Pagada el' : 'Vencimiento'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((f: any) => {
                const est = ESTADOS[f.estado] || { label: f.estado, color: 'default' };
                const vencida = f.estado === 'registrada' && f.proximo_vencimiento && f.proximo_vencimiento < hoy;
                return (
                  <TableRow key={f.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.proveedor_nombre}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{f.numero_proveedor || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {f.pedido_numero ? `Pedido ${f.pedido_numero}` : 'Sin pedido'} · {f.fecha}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{eur(f.total)}</TableCell>
                    <TableCell sx={{ color: vencida ? 'error.main' : 'inherit', fontWeight: vencida ? 700 : 400 }}>
                      {f.estado === 'pagada'
                        ? (f.fecha_pago || '—')
                        : `${f.proximo_vencimiento || '—'}${vencida ? ' · vencida' : ''}`}
                    </TableCell>
                    <TableCell><Chip size="small" label={est.label} color={est.color} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {f.archivo && (
                        <Button size="small" onClick={() => descargarArchivoFacturaProveedor(f.id, f.archivo_nombre)}>
                          📎
                        </Button>
                      )}
                      {f.estado !== 'pagada' && (
                        <Button size="small" variant={f.estado === 'pendiente_recibir' ? 'contained' : 'text'}
                          sx={f.estado === 'pendiente_recibir' ? { bgcolor: '#4f46e5' } : {}}
                          onClick={() => setRegistrar(f)}>
                          {f.estado === 'pendiente_recibir' ? 'Registrar' : 'Corregir'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Los pagos se registran en Tesorería; al completarse, la factura pasa a Pagadas.
      </Typography>

      <RegistrarFacturaProveedor open={!!registrar}
        factura={registrar === 'nueva' ? null : registrar}
        onClose={() => setRegistrar(null)} onGuardada={cargar} />
    </Box>
  );
}
